import React, { useEffect } from "react"

// Interaction events that indicate the visitor is actually using the page.
// Loading GTM off the back of one of these (rather than a blind timer) keeps
// the GTM/GA4/Ads tag burst from landing in the middle of the visitor's first
// real tap or scroll — a fixed setTimeout can fire mid-interaction and spike
// INP, since GTM bootstraps GA4 + both Ads container tags synchronously once
// it loads. Passive + once so these never block the events they're listening for.
const INTERACTION_EVENTS = ["pointerdown", "touchstart", "keydown", "scroll"]

const DelayedGTM = () => {
  useEffect(() => {
    // Inside an iframe (e.g. /thanks/ loaded by an embedded Zoho form after
    // submit) the parent page already runs GTM and receives the lead event;
    // a second container here would only log a duplicate pageview.
    if (window.self !== window.top) return undefined
    let loaded = false

    const loadGTM = () => {
      if (loaded) return
      loaded = true
      clearTimeout(fallbackTimer)
      INTERACTION_EVENTS.forEach(evt =>
        window.removeEventListener(evt, loadGTM)
      )

      // Standard GTM bootstrap: initialize the dataLayer array and record the
      // gtm.start timing event *before* the script tag is inserted, matching the
      // official install snippet, so GTM's own timing measurement is correct and
      // any dataLayer.push() calls made before the script loads don't throw.
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" })

      const script = document.createElement("script")
      script.src = `https://www.googletagmanager.com/gtm.js?id=GTM-58JZWQT`
      script.async = true

      // Insert the script into the document
      document.head.appendChild(script)

      // Note: no <noscript> fallback appended here — a noscript tag inserted via JS
      // can never reach an actual no-JS user (this whole effect only runs when JS is
      // enabled). A real static fallback lives in src/html.js instead.
    }

    INTERACTION_EVENTS.forEach(evt =>
      window.addEventListener(evt, loadGTM, { passive: true, once: true })
    )

    // Fallback for visitors who never interact (crawlers, pure readers) —
    // GTM still needs to fire eventually for pageview tracking.
    const fallbackTimer = setTimeout(loadGTM, 5000)

    return () => {
      clearTimeout(fallbackTimer)
      INTERACTION_EVENTS.forEach(evt =>
        window.removeEventListener(evt, loadGTM)
      )
    }
  }, [])

  return null
}

export default DelayedGTM
